import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Router } from '@angular/router';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import isEqual from 'lodash/isEqual';
import { ApiResponse } from 'src/app/models/api-response.model';
import { Patient, Roles } from 'src/app/models/patient.model';
import { User } from 'src/app/models/user.model';
import { environment } from 'src/environments/environment';
import { Md5 } from 'ts-md5';
import { TwilioService } from '../communications/twilio.service';
import { CareCallGuideFormCacheService } from '../care-call-guide/care-call-guide-form-cache.service';
import { FirestoreService } from '../firestore.service';
import { SnackService } from '../snack.service';
import { TokenService } from './token.service';
import { CreateUserDto, UserFactory } from './user.factory';

/** Shown when an account is locked after too many failed sign-ins (backend LoginLockoutService). */
export const ACCOUNT_LOCKED_MESSAGE =
  'This account is locked after too many failed sign-in attempts. Try again in 15 minutes or contact an administrator.';

/**
 * Firebase Auth codes for a wrong email/password pair. With email enumeration protection on, Firebase
 * answers `auth/invalid-credential` (or `auth/invalid-login-credentials` on older SDKs) instead of
 * the two specific codes, so all of them are counted.
 */
const WRONG_CREDENTIAL_CODES = new Set([
  'auth/wrong-password',
  'auth/user-not-found',
  'auth/invalid-credential',
  'auth/invalid-login-credentials',
]);

/**
 * True for a lockout refusal: the backend answers 423 with `error: 'ACCOUNT_LOCKED'` (from
 * `auth/authorize`, or from `auth/login-failed` via our own rethrown message).
 */
export const isAccountLockedError = (error: any): boolean => {
  if (!error) {
    return false;
  }
  if (error.status === 423 || error.error?.error === 'ACCOUNT_LOCKED') {
    return true;
  }
  return typeof error.message === 'string' && error.message.includes('ACCOUNT_LOCKED');
};

/** Error code for an invitation sign-up whose link carries no invitation id. */
export const INVITATION_LINK_MISSING = 'invitation-link-missing';

/** What the sign-up form shows when an invitation sign-up fails, by cause. */
export const providerSignupErrorMessage = (error: any): string => {
  if (error?.code === 'auth/email-already-in-use') {
    return 'This email address is already registered. Sign in instead, or use the password you chose when you first registered.';
  }
  if (error?.code === INVITATION_LINK_MISSING) {
    return 'This invitation link is out of date. Ask for a new invitation and use the link in that email.';
  }
  // 403 means either no invitation for this email or one whose inviter may no longer grant it; the
  // server's message says which, and only the first is fixed by using a different address.
  if (error?.status === 403) {
    return (
      error.error?.message ??
      'No pending invitation was found for this email. Use the address the invitation was sent to, or ask for a new invitation.'
    );
  }
  if (error?.status === 409 && error.error?.message) {
    return error.error.message;
  }
  return `Error creating your account: ${error?.error?.message ?? error?.message ?? 'please try again'}`;
};

/**
 * The backend refused a session for lack of a second factor, and this account has none enrolled
 * yet. Thrown by `signIn` so the login page can run the mandatory enrollment instead of an error.
 * It only happens once the backend enforces staff MFA (toggle `security_config/mfa` ON), so the
 * web never asks for an enrollment the backend does not require.
 */
export const MFA_ENROLLMENT_REQUIRED = 'MFA_ENROLLMENT_REQUIRED';

/** Shown when the backend refuses a staff session that did not pass the second factor. */
export const MFA_REQUIRED_MESSAGE = 'Your account requires two-step verification. Please sign in again to complete it.';

/**
 * Shown when the backend refuses a sign-in whose second factor is not the staff phone on record
 * (403 MFA_PHONE_MISMATCH), e.g. a number enrolled outside this app. Only support can fix it.
 */
export const MFA_PHONE_MISMATCH_MESSAGE =
  "The verification phone doesn't match the one on file. Please contact support.";

/** Shown when Firebase asks for the second factor but the SDK did not hand us a way to resolve it. */
export const MFA_RESOLVER_MISSING_MESSAGE =
  'Two-step verification could not start. Please reload the page and try again, or contact support.';

/** Shown when the backend could not run the MFA check (503); the session is left alone. */
export const MFA_CHECK_UNAVAILABLE_MESSAGE =
  'We could not verify your sign-in right now. Please try again in a moment.';

/**
 * The backend's staff-without-second-factor refusal: 401 with `message: 'MFA_REQUIRED'`. The
 * message is the contract (not a `code` field), because HttpExceptionFilter keeps only `message`.
 */
export const isMfaRequiredError = (error: any): boolean =>
  error?.status === 401 && error?.error?.message === 'MFA_REQUIRED';

/** The backend's refusal of a second factor that is not the staff phone on record. */
export const isMfaPhoneMismatchError = (error: any): boolean =>
  error?.status === 403 && error?.error?.message === 'MFA_PHONE_MISMATCH';

/**
 * Whether the account counts as staff, mirroring the backend's mandatory-MFA rule. Closed by
 * default: no roles, or any role besides `isPatient`, is staff. UI only (e.g. the read-only staff
 * phone); whether a sign-in needs MFA is decided by the backend's MFA_REQUIRED answer.
 */
export const isStaff = (user: { roles?: Roles } | null | undefined): boolean => {
  const roles = user?.roles;
  if (!roles) {
    return true;
  }
  const hasStaffRole = Object.entries(roles).some(([role, enabled]) => role !== 'isPatient' && enabled === true);
  return hasStaffRole || !roles.isPatient;
};

/**
 * The staff phone on record as the E.164 US number MFA enrolls, or null when there is none usable.
 * Accepts the shapes stored in `users.phone` (`+15555555555`, `555-555-5555`, `(555) 555-5555`).
 */
export const toUsMfaPhone = (phone: unknown): string | null => {
  if (typeof phone !== 'string') {
    return null;
  }
  let digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  return digits.length === 10 ? `+1${digits}` : null;
};

/** Result of asking the backend to send the email verification that MFA enrollment requires. */
export type EmailVerificationOutcome = 'sent' | 'alreadyVerified' | 'throttled' | 'failed';

/** Marks a refresh the backend could not check (503), as opposed to a refused one. */
const CHECK_UNAVAILABLE = Symbol('CHECK_UNAVAILABLE');

@Injectable({
  providedIn: 'root',
})
export class NewAuthService {
  client_id: string;
  client_npi: string;
  user: Patient;

  /**
   * The authenticated user's Firestore document, as ONE shared stream.
   *
   * Declared once here rather than assigned inside the login paths. It used to be rebuilt in both
   * `validateAuthentication` and `signInUsingToken` — the latter inside a `switchMap`, which only
   * runs if something subscribes — so whether `user$` existed at all depended on how the session
   * started and on subscription timing.
   *
   * - `distinctUntilChanged` on the uid stops an authState re-emission for the same user (e.g. a
   *   token refresh) from tearing down and re-creating the document listener.
   * - `isEqual` after the switchMap drops snapshots whose content did not change. Reference
   *   equality would be useless here: `valueChanges()` hands back a brand-new object each snapshot.
   * - `shareReplay` multicasts, so every subscriber shares a single Firestore listener instead of
   *   opening its own, and late subscribers still receive the current value.
   */
  readonly user$: Observable<Patient> = this.afAuth.authState.pipe(
    map((authUser) => authUser?.uid ?? null),
    distinctUntilChanged(),
    switchMap((uid) => (uid ? this.db.doc<User>(`users/${uid}`).valueChanges() : of(null))),
    distinctUntilChanged(isEqual),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * `user$` restricted to a signed-in user — never emits the signed-out `null`.
   *
   * `user$` has to emit `null` on sign-out: the shell and home page use its falsy value to hide the
   * user menu, so a stream that stayed on the previous user would leave that menu up after logout,
   * which on a shared clinical workstation is worse than a console error.
   *
   * But `signOut()` awaits `afAuth.signOut()` before it navigates to `/login`, so that `null`
   * reaches every live subscriber while the clinical components are still mounted. Consumers that
   * dereference the user directly (`user.twilio_line`, `user.user_id`) throw on it. Subscribe to
   * this instead of adding a guard in each one.
   *
   * Shares the same upstream listener as `user$` — this is a filter on the multicast, not a second
   * Firestore subscription.
   */
  readonly currentUser$: Observable<Patient> = this.user$.pipe(filter((user) => !!user));

  isLoggedSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isLoggedObservable$: Observable<boolean> = this.isLoggedSubject.asObservable();
  needsTakeOverSubject: Subject<boolean> = new Subject<boolean>();
  needsTakeOverObservable$: Observable<boolean> = this.needsTakeOverSubject.asObservable();
  mfaResolver;
  /** A staff sign-in or sign-up is waiting on the mandatory MFA enrollment (see MfaEnrollmentFlowService). */
  mfaEnrollmentPending = false;
  emailVerified = false;
  emailsAlreadyRegistered = [];

  private authenticated = false;
  private tempCreds: { email: string; password: string } = { email: '', password: '' };
  private twilioInitialized = false;

  constructor(
    public afAuth: AngularFireAuth,
    private db: AngularFirestore,
    private tokenService: TokenService,
    private userFactory: UserFactory,
    private httpClient: HttpClient,
    private router: Router,
    private snackService: SnackService,
    private readonly fireFns: AngularFireFunctions,
    private fsService: FirestoreService,
    private twilio: TwilioService,
    private careCallGuideFormCache: CareCallGuideFormCacheService
  ) {
    this.afAuth.onAuthStateChanged(async (user) => {
      if (user) {
        this.emailVerified = user.emailVerified;
      }
    });
  }

  // check if user has mfa enabled
  async isMfaEnabled(): Promise<boolean> {
    const user = await this.afAuth.currentUser;
    return user?.multiFactor.enrolledFactors.length > 0;
  }

  emitTakeOverStatus(): void {
    if (this.emailVerified) {
      this.needsTakeOverSubject.next(false);
    } else {
      this.needsTakeOverSubject.next(true);
    }
  }

  setNeedsTakeOver(value: boolean): void {
    this.needsTakeOverSubject.next(value);
  }

  async resolveMFA(userCredential: firebase.auth.UserCredential) {
    if (this.mfaResolver) {
      try {
        await this.validateAuthentication(this.tempCreds.email, this.tempCreds.password, userCredential);
      } catch (error) {
        if (
          [
            ACCOUNT_LOCKED_MESSAGE,
            MFA_REQUIRED_MESSAGE,
            MFA_PHONE_MISMATCH_MESSAGE,
            MFA_CHECK_UNAVAILABLE_MESSAGE,
          ].includes(error?.message)
        ) {
          throw error;
        }
        throw new Error('Error validating authentication');
      }
      this.checkIfRoute(true);
      return this.user;
    }
  }

  async signIn(email: string, password: string): Promise<Patient> {
    // throw an error if the user is already authenticated
    if (this.authenticated) {
      throw new Error('Already authenticated');
    }

    // authenticate the user with angular fire
    let userCredential: firebase.auth.UserCredential;
    try {
      userCredential = await this.afAuth.signInWithEmailAndPassword(email, password);
    } catch (e) {
      if (e.code === 'auth/multi-factor-auth-required') {
        if (!e.resolver) {
          // auth-compat only attaches the resolver when the error passes `instanceof FirebaseError`,
          // which fails silently if the bundle carries more than one copy of @firebase/util.
          console.error('Firebase MFA error arrived without a resolver', e);
          throw new Error(MFA_RESOLVER_MISSING_MESSAGE);
        }
        this.tempCreds = { email, password };
        this.mfaResolver = e.resolver;
        throw new Error('Multi-factor authentication required');
      }
      if (WRONG_CREDENTIAL_CODES.has(e.code)) {
        // Firebase checked the password, so the backend never sees this failure unless we report it.
        const { locked } = await this.tokenService.reportFailedLogin(email);
        if (locked) {
          throw new Error(ACCOUNT_LOCKED_MESSAGE);
        }
      }
      throw e;
    }

    await this.validateAuthentication(email, password, userCredential, { allowMfaEnrollment: true });
    this.checkIfRoute(true);

    return this.user;
  }

  /**
   * What the mandatory enrollment needs about the signed-in (single-factor) staff user: the phone
   * on record, and whether Firebase will allow enrolling at all (it refuses an unverified email).
   */
  async getMfaEnrollmentContext(): Promise<{ signedIn: boolean; phoneNumber: string | null; emailVerified: boolean }> {
    const firebaseUser = await this.afAuth.currentUser;
    // The cached user can predate an email verification done elsewhere (e.g. the sign-up link).
    await firebaseUser?.reload().catch(() => undefined);
    if (!this.user && firebaseUser) {
      // Not loaded when enrollment starts from the sign-up email link rather than from signIn.
      this.user = (await this.db.collection('users').doc(firebaseUser.uid).ref.get()).data() as User;
    }
    return {
      signedIn: !!firebaseUser,
      phoneNumber: toUsMfaPhone(this.user?.phone),
      emailVerified: !!firebaseUser?.emailVerified,
    };
  }

  /**
   * Asks the backend to email the signed-in user a Firebase verification link with the Welby template,
   * a prerequisite for enrolling MFA. The user has no Welby session yet, so the call is authenticated
   * with the Firebase ID token, and the backend takes the address from that token, never from us.
   */
  async sendMfaEmailVerification(): Promise<EmailVerificationOutcome> {
    const firebaseUser = await this.afAuth.currentUser;
    if (!firebaseUser) {
      return 'failed';
    }
    try {
      const firebaseIdToken = await firebaseUser.getIdToken(true);
      const response = await this.httpClient
        .post<{ sent?: boolean; alreadyVerified?: boolean }>(
          `${environment.welbyEndpoint}/api/v1/auth/send-email-verification`,
          { firebaseIdToken }
        )
        .toPromise();
      if (response?.alreadyVerified) {
        // Verified since the cached user was loaded: refresh it so enrolling sees the new state.
        await firebaseUser.reload().catch(() => undefined);
        await firebaseUser.getIdToken(true).catch(() => undefined);
        return 'alreadyVerified';
      }
      return 'sent';
    } catch (error) {
      console.error('Could not send the verification email', error);
      return error?.status === 429 ? 'throttled' : 'failed';
    }
  }

  /**
   * Finishes a sign-in that `signIn` paused with MFA_ENROLLMENT_REQUIRED, once the second factor is
   * enrolled. Returns false when the password is no longer in memory (e.g. the enrollment ran from an
   * email link in another tab), in which case the caller should send the user to sign in again.
   */
  async completeSignInAfterEnrollment(): Promise<boolean> {
    const firebaseUser = await this.afAuth.currentUser;
    if (!firebaseUser || !this.tempCreds?.email || !this.tempCreds?.password) {
      return false;
    }
    await this.validateAuthentication(this.tempCreds.email, this.tempCreds.password, {
      user: firebaseUser,
    } as firebase.auth.UserCredential);
    this.mfaEnrollmentPending = false;
    await this.checkIfRoute(true);
    return true;
  }

  async signUpAsProvider(
    email: string,
    password: string,
    dto: CreateUserDto,
    routeAfterSignUp: boolean = true
  ): Promise<Patient> {
    if (this.authenticated) {
      throw new Error('Already authenticated');
    }

    try {
      // The backend requires the invitation id from the email link. A link without it (sent before the
      // id was added) cannot succeed, so stop before creating a Firebase account that would be orphaned.
      if (!dto.invitationId) {
        throw Object.assign(new Error(INVITATION_LINK_MISSING), { code: INVITATION_LINK_MISSING });
      }
      const { userCredential, created } = await this.createOrResumeProviderAccount(email, password);
      dto.uid = userCredential.user.uid;
      dto.practice = 'none';
      dto.practiceAdmin = false;
      dto.provider = true;
      dto.patient = false;
      dto.pwHash = Md5.hashStr(password);

      try {
        this.user = await this.completeProviderSignup(userCredential, dto);
      } catch (error) {
        // Without its users document the account cannot sign in, and it would block a retry with
        // `email-already-in-use`. Remove one created just now. A resumed account predates this attempt,
        // so it is kept, but its Firebase session is ended: `user$` follows afAuth.authState, and must not
        // show a signed-in user whose account is still incomplete.
        if (created) {
          await userCredential.user.delete().catch(() => undefined);
        } else {
          await this.afAuth.signOut().catch(() => undefined);
        }
        throw error;
      }

      if (this.user?.client_responsible_id) {
        await this.setEmailVerifiedInTrue(dto);
      }
      const hasSession = await this.startSignUpSession(email, password, userCredential);
      if (hasSession) {
        this.checkIfRoute(true);
      }
      return this.user;
    } catch (error) {
      this.snackService.genericSnackBar(providerSignupErrorMessage(error), ['error-snackbar']);
      throw error;
    }
  }

  async signUp(
    email: string,
    password: string,
    isProvider: boolean,
    dto: CreateUserDto,
    routeAfterSignUp: boolean = true
  ): Promise<Patient | any> {
    if (this.authenticated) {
      throw new Error('Already authenticated');
    }

    try {
      // Create the user with angular fire
      const userCredential: firebase.auth.UserCredential = await this.afAuth.createUserWithEmailAndPassword(
        email,
        password
      );

      this.client_id = await this.userFactory.createNewClient(email, dto);

      if (isProvider) {
        dto.clientId = this.client_id;
        dto.uid = userCredential.user.uid;
        dto.practiceAdmin = true;
        dto.provider = true;
        dto.patient = false;

        dto.pwHash = Md5.hashStr(password);
      } else {
        dto.uid = userCredential.user.uid;
        dto.practice = 'none';
        dto.practiceAdmin = false;
        dto.provider = false;
        dto.patient = true;
        dto.pwHash = Md5.hashStr(password);
      }

      const user = await this.userFactory.createUserAccount(email, dto);

      if (user && user.client_responsible_id) {
        await this.setEmailVerifiedInTrue(dto);
      }

      if (await this.startSignUpSession(email, password, userCredential)) {
        this.checkIfRoute(true);
      }

      return user;
    } catch (error) {
      console.error(error);
      this.snackService.genericSnackBar(`Error in signIn: ${error.message}`, ['error-snackbar']);
      throw error;
    }
  }

  async validateAuthentication(
    email: string,
    password: string,
    userCredential: firebase.auth.UserCredential,
    { allowMfaEnrollment = false }: { allowMfaEnrollment?: boolean } = {}
  ): Promise<Patient> {
    // add a 1 second wait to ensure the user is authenticated
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // Store the user in a user variable
      const userDoc = await this.db.collection('users').doc(userCredential.user.uid).ref.get();
      this.user = userDoc.data() as User;
      await userDoc.ref.set({ pw_hash: Md5.hashStr(password) }, { merge: true });

      // A fresh ID token, so it carries the second factor just resolved or enrolled. The backend
      // uses it to decide whether this session passed MFA.
      const firebaseIdToken = await this.getFreshIdToken(userCredential);

      // Store the access token in the local storage

      const tokenResult = await this.tokenService.requestNewToken(email, Md5.hashStr(password), firebaseIdToken);

      if (!tokenResult.access_token) {
        throw new Error('Error validating authentication');
      }

      // `user$` is a shared stream declared on the class — nothing to wire up per login.
      // Set the authenticated flag to true
      this.authenticated = true;

      // Initialize Twilio for Welby Clinical users
      this.initializeTwilioForWelbyClinical();

      return this.user;
    } catch (error) {
      if (isAccountLockedError(error)) {
        // Firebase accepted the password but the backend refused a locked account: do not leave the
        // user half signed in to Firebase with no Welby token.
        await this.afAuth.signOut().catch(() => undefined);
        throw new Error(ACCOUNT_LOCKED_MESSAGE);
      }
      if (isMfaRequiredError(error)) {
        // The backend enforces staff MFA and this account has no factor yet: keep the Firebase
        // session, which enrolling needs, and let the caller run the enrollment (cancelling signs out).
        if (allowMfaEnrollment && !(await this.isMfaEnabled())) {
          this.tempCreds = { email, password };
          this.mfaEnrollmentPending = true;
          throw new Error(MFA_ENROLLMENT_REQUIRED);
        }
        // Same reasoning as the lock: no half-signed-in Firebase session without a Welby token.
        await this.afAuth.signOut().catch(() => undefined);
        throw new Error(MFA_REQUIRED_MESSAGE);
      }
      if (isMfaPhoneMismatchError(error)) {
        // Not something the user can retry or re-enroll their way out of: support has to fix it.
        await this.afAuth.signOut().catch(() => undefined);
        throw new Error(MFA_PHONE_MISMATCH_MESSAGE);
      }
      if (error?.status === 503) {
        await this.afAuth.signOut().catch(() => undefined);
        throw new Error(MFA_CHECK_UNAVAILABLE_MESSAGE);
      }
      throw new Error('Error validating authentication');
    }
  }

  async signOut(): Promise<boolean> {
    this.twilio.disconnectDevice();
    this.careCallGuideFormCache.clearAll();
    await this.afAuth.signOut();
    this.tokenService.destroyToken();
    this.authenticated = false;
    this.mfaEnrollmentPending = false;
    this.tempCreds = { email: '', password: '' };
    this.twilioInitialized = false;
    this.router.navigate(['login']);
    this.isLoggedSubject.next(false);
    return true;
  }

  doLandingPageRoute(): Promise<boolean> {
    if (this.user?.roles?.isClient) {
      return this.router.navigateByUrl('clinical');
    } else if (this.user?.roles?.isPracticeAdmin) {
      return this.router.navigateByUrl('practice');
    }
  }

  sendVerificationEmailForProvider(
    email: string,
    clientId: string,
    userId: string,
    first_name: string,
    last_name: string
  ): Promise<void> {
    const baseUrl = window.location.href.match(/^(.*?\/\/.*?\/).*$/)[1];
    const url = `${baseUrl}new-provider-signup?step=2&clientId=${clientId}&userId=${userId}`;
    const callable = this.fireFns.httpsCallable('firebaseValidateEmail');
    return callable({ email, link: url, first_name, last_name }).pipe(take(1)).toPromise();
  }

  sendVerificationEmail(email: string): Promise<void> {
    const baseUrl = window.location.href.match(/^(.*?\/\/.*?\/).*$/)[1];
    const url = `${baseUrl}login`;
    return this.afAuth.sendSignInLinkToEmail(email, { url, handleCodeInApp: true });
  }

  resetPassword(email: string = null): Promise<void> {
    return this.afAuth.sendPasswordResetEmail(email ?? this.user.email);
  }

  forgotPassword(): Promise<void> {
    return this.afAuth.sendPasswordResetEmail(this.user.email);
  }

  signInUsingToken(uid): Observable<any> {
    // Renew token
    return this.httpClient
      .post(environment.welbyEndpoint + '/api/v1/auth/refresh-access-token', {
        accessToken: this.tokenService.getToken(),
        uid,
      })
      .pipe(
        catchError((error) => {
          // A 503 means the backend could not run its MFA check, not that the session is bad:
          // signing out here would log every staff user out during a Firestore blip.
          if (error?.status === 503) {
            this.snackService.genericSnackBar(MFA_CHECK_UNAVAILABLE_MESSAGE, ['warn-snackbar'], 5000);
            return of(CHECK_UNAVAILABLE);
          }
          return this.signOut().then(() => false);
        }),
        switchMap((response: any) => {
          if (response === CHECK_UNAVAILABLE) {
            // Not authenticated for this navigation, but the stored session is kept for a retry.
            return of(false);
          }
          if (!response) {
            return this.signOut().then(() => false);
          }

          // Store the enw token
          this.tokenService.setToken(response.access_token);
          // Set the authenticated flag to true
          this.authenticated = true;
          // store the user object from the response in the user variable
          this.user = response.user;
          // `user$` is a shared stream declared on the class — nothing to wire up per session restore.

          // Initialize Twilio for Welby Clinical users
          this.initializeTwilioForWelbyClinical();

          return of(true);
        })
      );
  }

  async checkingIfEmailIsAlreadyInFB(email: string, password = ' ') {
    try {
      if (this.emailsAlreadyRegistered.findIndex((emailSaved) => email === emailSaved) !== -1) {
        return false;
      }
      await this.afAuth.signInWithEmailAndPassword(email, password);
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        this.emailsAlreadyRegistered.push(email);
        return false;
      } else {
        return true;
      }
    }
  }

  async setEmailVerifiedInTrue(dto: CreateUserDto) {
    const { phoneNumber, ...withoutNumber } = dto;
    const response = await this.autoValidateEmail(withoutNumber).catch((error) => {
      this.snackService.genericSnackBar('Error validating email', ['error-snackbar']);
      console.error(error);
    });
    this.emailVerified = response?.success ? true : false;
  }

  async checkIfRoute(routeAfterSignUp: boolean) {
    if (routeAfterSignUp) {
      // rout the user to the correct landing page
      const isRedirected = await this.doLandingPageRoute();
      if (isRedirected) {
        this.isLoggedSubject.next(true);
        this.tempCreds = null;
      }
    }
  }

  getAfAuthUser(): Promise<firebase.User> {
    return this.afAuth.user.toPromise();
  }

  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this.authenticated) {
      this.isLoggedSubject.next(true);
      return of(true);
    }

    // Check the access token availability
    if (!this.tokenService.getToken()) {
      this.isLoggedSubject.next(false);
      return of(false);
    }

    // Check if the token is expired
    if (this.tokenService.tokenIsExpired()) {
      this.isLoggedSubject.next(false);
      return of(false);
    }

    // Check if user is authenticated in firebase
    return this.afAuth.authState.pipe(
      take(1),
      switchMap((user) => {
        if (user) {
          this.isLoggedSubject.next(true);
          return this.signInUsingToken(user.uid);
        } else {
          this.isLoggedSubject.next(false);
          return of(false);
        }
      })
    );
  }

  isClientAccount() {
    return this.user
      ? of(this.user.roles.isClient)
      : this.afAuth.authState.pipe(
          take(1),
          switchMap(async (user) => {
            if (user) {
              const userDoc = await this.db.collection('users').doc(user.uid).ref.get();
              const currentUser = userDoc.data() as Patient;
              return of(currentUser.roles.isClient);
            } else {
              return of(false);
            }
          })
        );
  }

  getUserRoles(): Observable<Roles | null> {
    return this.user
      ? of(this.user.roles)
      : (this.afAuth.authState.pipe(
          take(1),
          switchMap(async (user) => {
            if (user) {
              const userDoc = await this.db.collection('users').doc(user.uid).ref.get();
              const currentUser = userDoc.data() as Patient;
              return currentUser.roles;
            } else {
              return null;
            }
          })
        ) as Observable<Roles | null>);
  }

  async autoValidateEmail(userDto): Promise<any> {
    const url = `${environment.firebaseURL}/backendUser/autoValidateEmail`;
    return this.httpClient.post(url, userDto).toPromise();
  }

  /**
   * Change the email login of a patient
   *
   * @param email new Email
   * @param patientId patient id
   * @returns promise<void>
   */
  changeEmail(email: string, patientId: string): Promise<any> {
    const callable = this.fireFns.httpsCallable('change_auth_email');
    const data = { email, uid: patientId };
    return callable(data).pipe(take(1)).toPromise();
  }

  getIfUserHasAIAuthorization(): Observable<boolean> {
    const email = this.user.email;
    return this.db
      .collection('ai_active_users')
      .doc('ai-messages')
      .get()
      .pipe(
        map((doc) =>
          doc.exists
            ? (doc.data() as { active: string[] }).active.some((userEmail: string) => userEmail === email)
            : false
        )
      );
  }

  getAiMessageValue(userId: string): Observable<boolean> {
    return this.db
      .collection('users')
      .doc(userId)
      .get()
      .pipe(map((doc) => (doc.data() as { enableAImessage?: boolean })?.enableAImessage || false));
  }
  async saveIndexDragAndDrop(userId: string, twilioLines: string | string[]): Promise<void> {
    await this.fsService.col('users').doc(userId).update({
      twilio_line: twilioLines,
    });
  }

  initializeTwilioClient(identity: string): void {
    // Rejection handler as the second argument, not .catch(): a throw inside initializeTwilio is not a
    // token failure and must reach the global ErrorHandler as itself.
    this.twilio.getToken(identity).then(
      (token) => this.twilio.initializeTwilio(token, identity),
      (error) => this.twilio.reportTokenFetchFailure(error)
    );
  }

  private initializeTwilioForWelbyClinical(): void {
    if (this.twilioInitialized) {
      return;
    }

    if (this.user?.roles?.isWelbyClinical) {
      const identity = this.user.user_id;
      this.initializeTwilioClient(identity);
      this.twilioInitialized = true;
    }
  }

  /**
   * Opens the backend session right after an account is created. When the backend enforces staff
   * MFA it opens none until a second factor is enrolled (`mfaEnrollmentPending` is set instead);
   * there is deliberately no sign-up exemption, since self sign-up can create staff accounts.
   * Returns whether a session was opened.
   */
  private async startSignUpSession(
    email: string,
    password: string,
    userCredential: firebase.auth.UserCredential
  ): Promise<boolean> {
    try {
      await this.validateAuthentication(email, password, userCredential, { allowMfaEnrollment: true });
      return true;
    } catch (error) {
      if (error?.message === MFA_ENROLLMENT_REQUIRED) {
        return false;
      }
      throw error;
    }
  }

  /**
   * A force-refreshed Firebase ID token for the credential's user, or undefined if it cannot be read.
   * Sent without it, the backend treats the sign-in as single-factor, which is the safe side.
   */
  private async getFreshIdToken(userCredential: firebase.auth.UserCredential): Promise<string | undefined> {
    try {
      return await userCredential.user.getIdToken(true);
    } catch (error) {
      console.error('Could not read a fresh Firebase ID token', error);
      return undefined;
    }
  }

  /**
   * Creates the Firebase account, or resumes one an earlier attempt created and then failed to finish:
   * before the server-side sign-up, a rejected `users` write left the account behind with no document,
   * and every retry then stopped at `auth/email-already-in-use`. Resuming needs the same password and a
   * document that still has no roles, so it can only ever finish a sign-up, never take over an account.
   */
  private async createOrResumeProviderAccount(
    email: string,
    password: string
  ): Promise<{ userCredential: firebase.auth.UserCredential; created: boolean }> {
    try {
      return { userCredential: await this.afAuth.createUserWithEmailAndPassword(email, password), created: true };
    } catch (error) {
      if (error?.code !== 'auth/email-already-in-use') {
        throw error;
      }
      let userCredential: firebase.auth.UserCredential;
      try {
        userCredential = await this.afAuth.signInWithEmailAndPassword(email, password);
      } catch {
        // Wrong password, or a second factor is enrolled: an account someone actually uses.
        throw error;
      }
      const existing = await this.db.collection('users').doc(userCredential.user.uid).ref.get();
      if (existing.exists && existing.get('roles')) {
        await this.afAuth.signOut().catch(() => undefined);
        throw error;
      }
      return { userCredential, created: false };
    }
  }

  /**
   * Has the backend create the `users` document from the stored invitation. The browser cannot write it:
   * the `users` create rule refuses a self-create that seeds a clinic or `isWelbyClinical`, which is what
   * an invitation grants. The clinic, roles, lead and licensure therefore come from `signup_requests` on
   * the server, which also consumes the invitation; only the name, phone and password hash go from here.
   */
  private async completeProviderSignup(userCredential: firebase.auth.UserCredential, dto: CreateUserDto): Promise<Patient> {
    const firebaseIdToken = await userCredential.user.getIdToken(true);
    await this.httpClient
      .post<ApiResponse<{ user_id: string }>>(`${environment.welbyEndpoint}/api/v1/auth/provider-signup`, {
        firebaseIdToken,
        invitationId: dto.invitationId,
        firstName: dto.fName,
        lastName: dto.lName,
        phoneNumber: dto.phoneNumber,
        pwHash: dto.pwHash,
        profileImageURL: environment?.defaultPhotoUrl ?? '',
      })
      .pipe(map((res) => res.data))
      .toPromise();
    return (await this.db.collection('users').doc(userCredential.user.uid).ref.get()).data() as Patient;
  }
}
